import { Activity, AppWindow, ClipboardList, Layers3, Workflow } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useContextStore } from "@/store/context";
import { useAgentSpecStudioStore } from "@/store/agentSpecStudio";
import { cn } from "@/utils/cn";

type AgentSpecSidebarSection = "overview" | "workflows" | "miniapps" | "executions" | "plans";

function sectionFromSearch(search: string): AgentSpecSidebarSection {
  const params = new URLSearchParams(search);
  const section = params.get("section");
  if (
    section === "workflows" ||
    section === "miniapps" ||
    section === "executions" ||
    section === "plans" ||
    section === "overview"
  ) {
    return section;
  }
  return params.has("planId") ? "plans" : "overview";
}

export default function AgentSpecSidebarNavigation({
  expanded,
  onNavigate,
}: {
  expanded: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const agentId = useContextStore((state) => state.deploymentId);
  const navigationAgentId = useAgentSpecStudioStore(
    (state) => state.navigationAgentId,
  );
  const storedResources = useAgentSpecStudioStore(
    (state) => state.navigationResources,
  );

  if (!expanded) return null;

  const resources =
    navigationAgentId === agentId
      ? storedResources
      : { workflows: [], miniapps: [] };
  const activeSection = sectionFromSearch(location.search);
  const selectedResourceId = new URLSearchParams(location.search).get(
    "resourceId",
  );
  const items = [
    {
      id: "overview" as const,
      icon: Layers3,
      label: t("agentSpec.agentOverview"),
    },
    {
      id: "workflows" as const,
      icon: Workflow,
      label: t("agentSpec.workflowsTitle"),
      count: resources.workflows.length,
    },
    {
      id: "miniapps" as const,
      icon: AppWindow,
      label: t("agentSpec.miniappsTitle"),
      count: resources.miniapps.length,
    },
    {
      id: "executions" as const,
      icon: Activity,
      label: t("agentSpec.executionsNavigation"),
    },
    {
      id: "plans" as const,
      icon: ClipboardList,
      label: t("agentSpec.plansNavigation"),
    },
  ];

  const hrefFor = (section: AgentSpecSidebarSection, resourceId?: string) => {
    const params = new URLSearchParams(location.search);
    params.set("section", section);
    if (resourceId) params.set("resourceId", resourceId);
    else params.delete("resourceId");
    return `${location.pathname}?${params.toString()}`;
  };

  return (
    <div className="ml-3 mt-2 space-y-1 border-l border-sidebar-border pl-3">
      <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-foreground/50">
        {t("agentSpec.agentView")}
      </p>
      {items.map((item) => {
        const active = activeSection === item.id;
        const itemResources =
          item.id === "workflows"
            ? resources.workflows
            : item.id === "miniapps"
              ? resources.miniapps
              : [];
        const defaultResourceId = itemResources[0]?.id;
        return (
          <div key={item.id}>
            <Link
              to={hrefFor(item.id, defaultResourceId)}
              onClick={onNavigate}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-agent-violet/10 font-medium text-agent-violet"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-foreground",
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.count !== undefined ? (
                <span className="min-w-6 rounded-full border border-sidebar-border px-1.5 py-0.5 text-center text-[11px] leading-none">
                  {item.count}
                </span>
              ) : null}
            </Link>
            {active && itemResources.length > 0 ? (
              <div className="mt-1 space-y-1 pl-6">
                {itemResources.map((resource) => (
                  <Link
                    key={resource.id}
                    to={hrefFor(item.id, resource.id)}
                    onClick={onNavigate}
                    aria-current={
                      selectedResourceId === resource.id ? "page" : undefined
                    }
                    className={cn(
                      "block truncate rounded-md px-2 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      selectedResourceId === resource.id ||
                        (!selectedResourceId &&
                          resource.id === defaultResourceId)
                        ? "bg-sidebar-accent font-medium text-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-foreground",
                    )}
                  >
                    {resource.title}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
